"use client"

import type React from "react"
import Image from "next/image"
import Link from "next/link"
import { HiArrowLeft, HiArrowRight } from "react-icons/hi2"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { useState, useEffect } from "react"
import { Plus_Jakarta_Sans } from "next/font/google"

import { apiGetNewsDetail, apiGetAllNews } from '@/services/newsHome/api';
import { NewsItem, GetAllNewsPayload } from '@/services/newsHome/type';
import { formatDateVN } from "@/utils/client/date"

// Cấu hình font chữ giao diện
const jakarta = Plus_Jakarta_Sans({
    subsets: ['vietnamese'],
    weight: ['400', '500', '600', '700'],
    display: 'swap'
})

// Hàm gộp className cho Tailwind
function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

// Interface props cho card tin tức
interface NewsCardProps {
    image: string | null
    category: string
    date: string
    title: string
    className?: string
}

// Component hiển thị thẻ tin tức
const NewsCard: React.FC<NewsCardProps> = ({
    image,
    category,
    date,
    title,
    className,
}) => {
    const isExternalImage = image && (image.startsWith('http://') || image.startsWith('https://'))

    return (
        <div className={cn("group flex flex-col gap-3 2xl:gap-4 min-[1920px]:gap-6 overflow-hidden h-full", className)}>
            <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-gray-50">
                {isExternalImage ? (
                    <img
                        src={image}
                        alt={title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                    />
                ) : (
                    <Image
                        src={image || "/placeholder.svg"}
                        alt={title}
                        fill
                        className="object-cover transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group-hover:scale-105"
                    />
                )}

                <div className="absolute top-3 left-3 rounded-full bg-[#005a32] px-3 py-1 min-[1920px]:px-4 min-[1920px]:py-2 text-[10px] min-[1920px]:text-base font-medium text-white shadow-sm">
                    {category}
                </div>
            </div>

            <div className="flex flex-col gap-2 min-[1920px]:gap-4 flex-1">
                <div className="text-xs 2xl:text-sm min-[1920px]:text-lg text-[#9F9F9F] font-normal">
                    Ngày đăng • <span className="text-[#2D3746]">{date}</span>
                </div>

                <h3 className={cn(
                    jakarta.className,
                    "line-clamp-2 text-base 2xl:text-lg min-[1920px]:text-2xl font-bold leading-snug text-[#2D3746] transition-colors duration-300 group-hover:text-[#005a32]"
                )}>
                    {title}
                </h3>
            </div>
        </div>
    )
}

// Cấu hình số lượng item trên mỗi trang
const ITEMS_PER_PAGE = 3

// Interface props cho section tin liên quan
interface OtherNewsSectionProps {
    slug: string;
}

// Component hiển thị danh sách tin liên quan
export const OtherNewsSection: React.FC<OtherNewsSectionProps> = ({ slug }) => {

    // State dữ liệu tin liên quan
    const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
    // State trạng thái loading
    const [isLoading, setIsLoading] = useState(true);
    // State trạng thái lỗi
    const [isError, setIsError] = useState(false);

    // State quản lý phân trang
    const [pageState, setPageState] = useState({ page: 0, direction: 'next' })
    const currentPage = pageState.page

    // Hàm tạo slug từ tiêu đề
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Effect lấy dữ liệu tin liên quan theo ID bài hiện tại
    useEffect(() => {
        if (!slug) return;

        const fetchRelatedData = async () => {
            setIsLoading(true);
            setIsError(false);
            try {
                // Lấy thông tin bài hiện tại để xác định loại tin
                const detailData = await apiGetNewsDetail(slug);

                if (!detailData || !detailData.isOK || !detailData.result) {
                    throw new Error("Không tìm thấy bài viết hiện tại");
                }

                const currentNews = detailData.result;
                const typeId = currentNews.newsTypeId;
                const currentId = currentNews.id;

                const payload: GetAllNewsPayload = {
                    keywords: '',
                    newsTypeId: typeId,
                    isPublic: 'true',
                    page: 1,
                    pageSize: 20
                };

                // Gọi API lấy danh sách tin theo loại
                const listData = await apiGetAllNews(payload);

                if (!listData || !listData.isOK || !listData.result) {
                    throw new Error("Lỗi khi tải danh sách tin liên quan");
                }

                // Lọc bỏ bài viết hiện tại khỏi danh sách liên quan
                let filteredItems = listData.result.items.filter(
                    (item) => item.id !== currentId
                );

                const targetTypeIds = [
                    Number(process.env.NEXT_PUBLIC_TRAVEL_ECO_ID),
                    Number(process.env.NEXT_PUBLIC_LOGISTICS_ECO_ID),
                    Number(process.env.NEXT_PUBLIC_HOSPITALITY_ECO_ID),
                    Number(process.env.NEXT_PUBLIC_INVESTMENT_ECO_ID)
                ];

                if (targetTypeIds.includes(typeId)) {
                    filteredItems = filteredItems.filter(
                        (item) => item.seoDescription === "Bài báo" || item.seoDescription === "Dự án" || item.seoDescription === "Dịch vụ"
                    );
                }

                setRelatedNews(filteredItems);

            } catch (error) {
                console.error("Lỗi fetch tin liên quan:", error);
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchRelatedData();
    }, [slug]);

    // Tính toán dữ liệu hiển thị theo trang
    const allNews = relatedNews;
    const totalPages = Math.ceil(allNews.length / ITEMS_PER_PAGE)

    const displayedNews = allNews.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    )

    // Hàm chuyển về trang trước
    const goToPrevPage = () => {
        setPageState(prev => ({
            page: Math.max(0, prev.page - 1),
            direction: 'prev'
        }))
    }

    // Hàm chuyển sang trang sau
    const goToNextPage = () => {
        setPageState(prev => ({
            page: Math.min(totalPages - 1, prev.page + 1),
            direction: 'next'
        }))
    }

    // Class style cho nút điều hướng phân trang
    const navButtonClasses = cn(
        "flex items-center justify-center size-7 2xl:size-9 min-[1920px]:size-12 rounded-full",
        "bg-transparent border border-gray-500",
        "text-gray-500 opacity-70 transition-all duration-300",
        "hover:border-black hover:text-black hover:scale-110 hover:opacity-100",
        "disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-gray-500 disabled:hover:scale-100 disabled:hover:opacity-30"
    )

    // Điều kiện ẩn section khi không có dữ liệu
    if (!isLoading && !isError && allNews.length === 0) {
        return null;
    }

    return (
        <section className="py-12 -mt-25 -mb-5 lg:-mb-20  px-4 xl:px-8 2xl:px-16 min-[1920px]:py-24 min-[1920px]:px-32 bg-white overflow-hidden">
            {/* Khai báo animation chuyển trang */}
            <style jsx>{`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInLeft {
                    from { opacity: 0; transform: translateX(-50px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-slide-next {
                    animation: slideInRight 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
                .animate-slide-prev {
                    animation: slideInLeft 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
                }
            `}</style>

            <div className="mx-auto max-w-4xl 2xl:max-w-6xl min-[1920px]:max-w-[110rem] relative">

                <div className="mb-5 min-[1920px]:mb-12 mt-6 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                    <div className="max-w-xl 2xl:max-w-3xl min-[1920px]:max-w-5xl space-y-2 min-[1920px]:space-y-4">
                        <h2 className={cn(
                            jakarta.className,
                            "text-2xl font-semibold text-[#2D3746] xl:text-3xl 2xl:text-4xl min-[1920px]:text-6xl tracking-tight"
                        )}>
                            Bài viết khác
                        </h2>
                    </div>

                    {/* Khối điều khiển phân trang */}
                    <div className="flex gap-3">
                        <button
                            onClick={goToPrevPage}
                            disabled={currentPage === 0 || isLoading || isError}
                            className={navButtonClasses}
                            aria-label="Previous page"
                        >
                            <HiArrowLeft className="size-3.5 2xl:size-4 min-[1920px]:size-5" />
                        </button>

                        <button
                            onClick={goToNextPage}
                            disabled={currentPage === totalPages - 1 || isLoading || isError || totalPages === 0}
                            className={navButtonClasses}
                            aria-label="Next page"
                        >
                            <HiArrowRight className="size-3.5 2xl:size-4 min-[1920px]:size-5" />
                        </button>
                    </div>
                </div>

                <div className="relative">
                    <div className="min-h-[350px] min-[1920px]:min-h-[600px]">
                        {/* Giao diện skeleton loading */}
                        {isLoading && (
                            <div className="grid grid-cols-1 gap-5 2xl:gap-10 min-[1920px]:gap-20 sm:grid-cols-2 xl:grid-cols-3">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="animate-pulse flex flex-col gap-3">
                                        <div className="aspect-[16/10] rounded-xl bg-gray-100" />
                                        <div className="space-y-2">
                                            <div className="h-3 w-28 rounded bg-gray-100" />
                                            <div className="h-5 w-full rounded bg-gray-100" />
                                            <div className="h-3 w-2/3 rounded bg-gray-100" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Giao diện trạng thái lỗi */}
                        {isError && (
                            <div className="flex h-[250px] items-center justify-center">
                                <p className="text-gray-500 text-sm font-medium">Không thể tải tin tức liên quan.</p>
                            </div>
                        )}

                        {/* Giao diện danh sách tin liên quan */}
                        {!isLoading && !isError && allNews.length > 0 && (
                            <div
                                key={currentPage}
                                className={cn(
                                    "grid grid-cols-1 gap-5 2xl:gap-10 min-[1920px]:gap-20 sm:grid-cols-2 xl:grid-cols-3",
                                    pageState.direction === 'next' ? 'animate-slide-next' : 'animate-slide-prev'
                                )}
                            >
                                {displayedNews.map((item) => {
                                    const slugTitle = createSlug(item.title || '');
                                    const postHref = `/news/${slugTitle}-${item.id}`;

                                    return (
                                        <Link href={postHref} key={item.id} className="block h-full hover:no-underline">
                                            <NewsCard
                                                image={item.image}
                                                category={item.newsTypeName || "Tin công ty"}
                                                date={formatDateVN(item.updatedDateTS)}
                                                title={item.title}
                                            />
                                        </Link>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default OtherNewsSection